import { auth, db } from '../firebase/config';
import { collection, doc, onSnapshot, query, where, serverTimestamp, addDoc, runTransaction, arrayUnion, writeBatch, getDocs, deleteDoc } from 'firebase/firestore';

// Mismos tipos que SIGNABLE_DOCUMENTS de signedDocumentService.js; no se importa
// de allí para no arrastrar backupService a todos los que cargan este servicio.
export const DOCUSIGN_DOC_TYPES = ['lor', 'spol'];

export const TICKET_STATES = ['pending', 'in_progress', 'waiting', 'resolved'];
export const TICKET_PRIORITIES = ['low', 'medium', 'high'];
export const actor = () => {
  const user = auth.currentUser;
  if (!user?.email) throw new Error('Authentication required');
  return { email: user.email, uid: user.uid };
};

// Uses the existing admin-only notifications collection. Events and their
// originating mutation are committed by the same batch/transaction.
export function appendActivity(writer, event) {
  const user = actor();
  writer.set(doc(collection(db, 'notifications')), {
    ...event, kind: 'activity', createdBy: user.email, createdByUid: user.uid,
    createdAt: serverTimestamp(), readBy: []
  });
}

export function subscribeWorkspace(kind, next, error) {
  return onSnapshot(query(collection(db, 'notifications'), where('kind', '==', kind)), snapshot => {
    next(snapshot.docs.map(item => ({ ...item.data(), id: item.id }))
      .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)));
  }, error);
}

// Activity del portal (no admin): los movimientos de paso de los reclamos a los que el usuario
// tiene acceso. Las consultas llevan los filtros que las reglas pueden probar (compañía + P.A o
// accessNames), una por compañía; se unen sin repetir. Solo trae movimientos nuevos: los anteriores
// no guardaban a quién pertenecen.
export function subscribeMyActivity({ companies, adjusterName, canViewAll }, next, error) {
  const base = companyName => [collection(db, 'notifications'), where('kind', '==', 'activity'),
    where('event', '==', 'claim_step'), where('companyName', '==', companyName)];
  const queries = (companies || []).flatMap(companyName => (canViewAll
    ? [query(...base(companyName))]
    : [query(...base(companyName), where('adjusterName', '==', adjusterName)),
       query(...base(companyName), where('accessNames', 'array-contains', adjusterName))]));
  const latest = new Map();
  const publish = () => {
    const merged = new Map();
    [...latest.values()].flat().forEach(item => merged.set(item.id, item));
    next([...merged.values()].sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)));
  };
  const stops = queries.map((q, index) => onSnapshot(q, snapshot => {
    latest.set(index, snapshot.docs.map(item => ({ ...item.data(), id: item.id })));
    publish();
  }, error));
  if (queries.length === 0) next([]);
  return () => stops.forEach(stop => stop());
}

// Avisos de DocuSign para quien no es admin (CSR Assistant). Las reglas solo le dejan
// leer esos eventos, así que cada consulta filtra por UN evento (no `kind` a secas).
const DOCUSIGN_EVENTS = ['docusign_signed', 'docusign_declined', 'docusign_voided'];
export function subscribeDocusignEvents(next, error) {
  const latest = {};
  const publish = () => next(Object.values(latest).flat()
    .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)));
  const unsubscribers = DOCUSIGN_EVENTS.map(event => onSnapshot(
    query(collection(db, 'notifications'), where('kind', '==', 'activity'), where('event', '==', event)),
    snapshot => { latest[event] = snapshot.docs.map(item => ({ ...item.data(), id: item.id })); publish(); },
    error
  ));
  return () => unsubscribers.forEach(unsubscribe => unsubscribe());
}

// claimRecordId (opcional): vincula el caso a un reclamo real, elegido con
// el buscador de "Nuevo caso o nota" (TeamWorkspace.jsx) — pedido explícito
// 2026-09-21, para poder abrir el reclamo real desde el caso ("Ir al
// reclamo") en vez de que "reference" sea solo texto suelto sin conectar.
export async function createTicket({ title, text, type, reference, priority, claimRecordId }) {
  const user = actor();
  if (!title.trim() || !text.trim() || title.length > 120 || text.length > 6000 || !['case', 'note'].includes(type)) throw new Error('Invalid ticket');
  const safePriority = TICKET_PRIORITIES.includes(priority) ? priority : 'medium';
  return addDoc(collection(db, 'notifications'), {
    kind: 'ticket', title: title.trim(), text: text.trim(), type,
    reference: (reference || '').trim().slice(0, 200), status: 'pending', priority: safePriority,
    claimRecordId: claimRecordId || null,
    createdBy: user.email, createdByUid: user.uid, createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(), history: []
  });
}

// Casos/notas de UN reclamo puntual — usado por ClaimCases.jsx, montado
// dentro de ClaimDetailPage.jsx (pedido explícito 2026-09-21: "cada caso y
// cada nota debe ser creada desde el reclamo abierto en My Claims"). El
// where('claimRecordId', '==', ...) es obligatorio para que firestore.rules
// autorice el query a un ajustador (ver canAccessClaimRecord arriba).
export async function getTicketsByClaimRecordId(claimRecordId) {
  const snapshot = await getDocs(query(
    collection(db, 'notifications'),
    where('kind', '==', 'ticket'),
    where('claimRecordId', '==', claimRecordId)
  ));
  return snapshot.docs.map(item => ({ id: item.id, ...item.data() }))
    .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
}

export async function updateTicket(id, { status, comment, priority }) {
  const user = actor();
  if (status !== undefined && !TICKET_STATES.includes(status)) throw new Error('Invalid status');
  if (priority !== undefined && !TICKET_PRIORITIES.includes(priority)) throw new Error('Invalid priority');
  if (comment !== undefined && (!comment.trim() || comment.length > 4000)) throw new Error('Invalid comment');
  const ref = doc(db, 'notifications', id);
  return runTransaction(db, async transaction => {
    const snapshot = await transaction.get(ref);
    if (!snapshot.exists() || snapshot.data().kind !== 'ticket') throw new Error('Ticket not found');
    const current = snapshot.data();
    if (status === current.status && comment === undefined && priority === undefined) return;
    if ((current.history || []).length >= 100) throw new Error('HISTORY_FULL');
    const entry = { createdBy: user.email, createdAt: new Date().toISOString() };
    const updates = { updatedAt: serverTimestamp() };
    if (status !== undefined) {
      Object.assign(entry, { from: current.status, to: status });
      updates.status = status;
      if (status !== current.status) appendActivity(transaction, { event: 'ticket_status', reference: current.title, targetId: id, status, previousStatus: current.status });
    }
    if (priority !== undefined) {
      Object.assign(entry, { priorityFrom: current.priority, priorityTo: priority });
      updates.priority = priority;
    }
    if (comment !== undefined) entry.text = comment.trim();
    updates.history = [...(current.history || []), entry];
    transaction.update(ref, updates);
  });
}

// Aviso de firma de DocuSign (creado por public/api/docusign-webhook.php,
// kind 'activity', event 'docusign_signed'): el admin elige a qué reclamo y a
// qué documento corresponde: 'lor', 'spol' o `closing:<id>` (un reclamo puede
// tener varios Closings, uno por cheque).
//
// NO lo marca como firmado: solo registra que DocuSign lo firmó
// (docusignSignedDate / docusignEnvelopeId) y, si aún no estaba enviado, lo
// pasa a 'sent'. Así "Subir firmado" sigue disponible y es quien respalda el
// PDF y, en un Closing, crea el cheque de cobro (attachSignedClosing). El PDF
// firmado no llega por el webhook.
export async function assignDocusignEvent(eventId, claimRecordId, docTarget) {
  const user = actor();
  const closingId = typeof docTarget === 'string' && docTarget.startsWith('closing:') ? docTarget.slice('closing:'.length) : null;
  if (!claimRecordId || !(DOCUSIGN_DOC_TYPES.includes(docTarget) || closingId)) throw new Error('Invalid assignment');
  const eventRef = doc(db, 'notifications', eventId);
  const targetRef = closingId ? doc(db, 'closings', closingId) : doc(db, 'claimRecords', claimRecordId);
  return runTransaction(db, async transaction => {
    const [eventSnap, targetSnap] = await Promise.all([transaction.get(eventRef), transaction.get(targetRef)]);
    const current = eventSnap.exists() ? eventSnap.data() : null;
    if (!current || current.event !== 'docusign_signed') throw new Error('Event not found');
    if (current.assigned) throw new Error('ALREADY_ASSIGNED');
    if (!targetSnap.exists()) throw new Error('Document not found');
    const target = targetSnap.data();
    // Un Closing solo se asigna a un closing de ESE reclamo.
    if (closingId && target.claimRecordId !== claimRecordId) throw new Error('Invalid assignment');
    const previous = closingId ? target : (target.signedDocuments?.[docTarget] || {});
    if (previous.status === 'signed') throw new Error('ALREADY_SIGNED');
    const prefix = closingId ? '' : `signedDocuments.${docTarget}.`;
    const signedAt = current.createdAt?.toDate?.() || new Date();
    const fields = {
      [`${prefix}docusignSignedDate`]: signedAt.toLocaleDateString('en-CA'),
      [`${prefix}docusignEnvelopeId`]: current.envelopeId || '',
      [`${prefix}updatedAt`]: serverTimestamp(),
      [`${prefix}updatedBy`]: user.email
    };
    if (previous.status !== 'sent') fields[`${prefix}status`] = 'sent';
    if (!closingId) fields.updatedAt = serverTimestamp();
    transaction.update(targetRef, fields);
    transaction.update(eventRef, {
      claimRecordId, docType: docTarget, assigned: true,
      assignedBy: user.email, assignedAt: serverTimestamp(), readBy: arrayUnion(user.uid)
    });
  });
}

export async function markActivityRead(items) {
  const { uid } = actor();
  const unread = items.filter(item => !(item.readBy || []).includes(uid));
  for (let offset = 0; offset < unread.length; offset += 400) {
    const batch = writeBatch(db);
    unread.slice(offset, offset + 400).forEach(item => batch.update(doc(db, 'notifications', item.id), { readBy: arrayUnion(uid) }));
    await batch.commit();
  }
}

// Borra UN solo ticket — admin-only (allow delete: if isAdmin() en
// firestore.rules, colección notifications). Pedido explícito 2026-09-22:
// hasta ahora solo existía "Borrar todo" (deleteAllTickets), no había forma
// de borrar un caso puntual ya creado.
export async function deleteTicket(id) {
  await deleteDoc(doc(db, 'notifications', id));
}

// Borra TODOS los tickets ("Casos y notas") — pedido explícito 2026-09-21,
// admin-only (mismo criterio que el resto de este servicio: notifications
// es admin-only en firestore.rules). No toca 'activity' (el feed
// auto-generado de Actividad) ni claimNotes (el respaldo de notas de
// reclamo) — solo los documentos con kind == 'ticket'. Acción irreversible,
// la UI la gatea detrás de un ConfirmModal explícito antes de llamar acá.
export async function deleteAllTickets() {
  const snapshot = await getDocs(query(collection(db, 'notifications'), where('kind', '==', 'ticket')));
  const ids = snapshot.docs.map(item => item.id);
  for (let offset = 0; offset < ids.length; offset += 400) {
    const batch = writeBatch(db);
    ids.slice(offset, offset + 400).forEach(id => batch.delete(doc(db, 'notifications', id)));
    await batch.commit();
  }
  return { deletedCount: ids.length };
}
